import { useRef } from "react";
import { Helmet } from "react-helmet";
import { Box, Grid, Stack, Typography } from "@mui/material";
import { useGetGalleryWallCollectionsQuery } from "../../generated/storefront";
import { Loader } from "../../components/Loader";
import { useAppState } from "../../state";
import CollectionCard from "../../components/Card/Collection";
import useLoadMoreOnScroll from "../../hooks/useLoadMoreOnScroll";
import Button from "../../components/Button";
import AutoResizeText from "../../components/AutoResizeText";
import TextLink from "../../components/TextLink";

const GalleryWalls = () => {
  const ref = useRef<HTMLDivElement>(null);
  const { isMobileScreen, toggleGalleryWallBuilderIsOpen } = useAppState();
  const { data, loading, fetchMore } = useGetGalleryWallCollectionsQuery();
  const galleryWalls = data?.collections.nodes || [];
  const pageInfo = data?.collections.pageInfo;
  const hasMore = pageInfo?.hasNextPage || false;

  const loadMore = async () => {
    if (!pageInfo?.hasNextPage || !pageInfo.endCursor) return;
    await fetchMore({
      variables: {
        afterCursor: pageInfo.endCursor,
      },
    });
  };

  const { loadingMore } = useLoadMoreOnScroll(ref, loadMore, hasMore, loading);

  return (
    <>
      <Helmet>
        <title>Gallery Walls | GoodMood Prints</title>
        <meta
          name="description"
          content="Explore our diverse collection of curated walls. Whether you choose to shop them as they are or seek inspiration to craft your own unique collection, we've got you covered."
        />
      </Helmet>
      <Stack gap={{ xs: 3, md: 4 }} paddingTop={2} paddingBottom={{ xs: 3, md: 5 }} position="relative">
        <Stack gap={{ xs: 1, md: 2 }} paddingX={{ xs: 2, md: 5 }} alignItems="center" width="100%">
          <AutoResizeText text="Gallery Walls" align="center" maxSize={isMobileScreen ? 50 : 85} />
          <Typography width={{ xs: "100%", md: "50%" }} align="center" fontSize={{ xs: 14, md: 20 }}>
            Explore our diverse collection of curated walls. Whether you choose to shop them as they are or seek inspiration to{" "}
            {isMobileScreen ? "craft your own" : <TextLink onClick={toggleGalleryWallBuilderIsOpen}>craft your own</TextLink>},
            we've got you covered
          </Typography>
        </Stack>
        <Box ref={ref} overflow="hidden">
          <Stack gap={3}>
            {loading ? (
              <Loader />
            ) : (
              <Box paddingX={{ xs: 2, md: 5 }}>
                <Grid container spacing={3} rowSpacing={{ xs: 3, md: 9 }}>
                  {galleryWalls.map((collection) => (
                    <Grid item key={collection.id} xs={12} md={4}>
                      <CollectionCard collection={collection} imageHeight={isMobileScreen ? "90vw" : "30vw"} />
                    </Grid>
                  ))}
                </Grid>
                {loadingMore ? (
                  <Loader />
                ) : hasMore ? (
                  <Stack width="100%" alignItems="center" padding={5}>
                    <Button onClick={loadMore}>Load more</Button>
                  </Stack>
                ) : null}
              </Box>
            )}
          </Stack>
        </Box>
      </Stack>
    </>
  );
};

export default GalleryWalls;
